import { DateTime } from 'luxon';
import { UNASSIGNED_FILTER_VALUE } from 'components/tasks/utils';
import { GoalKind, Objective } from 'services/objectives/types';
import { isDueDatePast, userNow } from 'utils/date';
import { applyRangeSelection, getItemsInRange } from 'utils/rangeSelection';

type ObjectiveStatus = NonNullable<Objective['status']>;

const OBJECTIVE_STATUSES: ObjectiveStatus[] = ['Cancelled', 'Completed', 'Doing', 'Failed', 'Todo'];

const objectiveStatus = (
  objective?: Pick<Objective, 'status'> | null,
): ObjectiveStatus => {
  if (objective?.status && OBJECTIVE_STATUSES.includes(objective.status)) {
    return objective.status;
  }
  return 'Todo';
};

const isClosedObjectiveStatus = (status?: string) => (
  status === 'Cancelled' || status === 'Completed' || status === 'Failed'
);

type Translate = (key: string, options?: Record<string, unknown>) => string;

const goalKindOf = (objective: { goalKind?: GoalKind | null }): GoalKind => (
  objective.goalKind || 'REACH'
);

const currentObjectiveValue = (objective: Objective): number | null => {
  if (objective.progress != null) {
    return objective.progress;
  }
  if (goalKindOf(objective) === 'REACH' && objective.initialProgress != null) {
    return objective.initialProgress;
  }
  return null;
};

const formatObjectiveProgress = (objective: Objective, t: Translate) => {
  const kind = goalKindOf(objective);
  const current = currentObjectiveValue(objective);
  const goal = objective.goal;
  if (kind === 'AROUND') {
    if (current == null) {
      return '-';
    }
    if (goal == null) {
      return String(current);
    }
    return t('objectiveAroundValue', { target: goal, value: current });
  }
  if (current == null) {
    return goal == null ? '-' : `- / ${goal}`;
  }
  if ((kind === 'HIGHEST' || kind === 'LOWEST') && goal == null) {
    return String(current);
  }
  if (goal == null) {
    return '-';
  }
  return `${current} / ${goal}`;
};

const getProgressRatio = (objective: Objective) => {
  const kind = goalKindOf(objective);
  const current = currentObjectiveValue(objective);
  const goal = objective.goal;
  if (current == null || goal == null) {
    return 0;
  }
  if (kind === 'LOWEST') {
    if (current <= goal) {
      return 1;
    }
    if (current === 0) {
      return 0;
    }
    return Math.min(1, Math.max(0, goal / current));
  }
  if (kind === 'AROUND') {
    const distance = Math.abs(current - goal);
    const tolerance = objective.goalTolerance ?? 0;
    if (distance <= tolerance) {
      return 1;
    }
    const scale = Math.max(tolerance, Math.abs(goal), 1);
    return Math.min(1, Math.max(0, 1 - ((distance - tolerance) / scale)));
  }
  const initial = kind === 'REACH' ? (objective.initialProgress || 0) : 0;
  const span = goal - initial;
  if (span === 0) {
    return current >= goal ? 1 : 0;
  }
  return Math.min(1, Math.max(0, (current - initial) / span));
};

const isObjectiveBehind = (objective: Objective, today = userNow()) => {
  if (isClosedObjectiveStatus(objectiveStatus(objective))) {
    return false;
  }
  const dueDate = objective.dueDate ? DateTime.fromISO(objective.dueDate) : null;
  if (!dueDate?.isValid || !isDueDatePast(objective.dueDate, today)) {
    return false;
  }
  const kind = goalKindOf(objective);
  const current = objective.progress ?? 0;
  const goal = objective.goal;
  if (kind === 'HIGHEST') {
    return goal != null && current < goal;
  }
  if (kind === 'LOWEST') {
    return goal != null && current > goal;
  }
  if (kind === 'AROUND') {
    if (goal == null || objective.progress == null) {
      return false;
    }
    return Math.abs(objective.progress - goal) > (objective.goalTolerance ?? 0);
  }
  return current < (goal || 0);
};

const getEstimatedCompletionDate = objective => {
  if (goalKindOf(objective) !== 'REACH') {
    return null;
  }
  const { goal } = objective;
  const currentProgress = objective.progress || 0;
  const initialProgress = objective.initialProgress || 0;
  const completionRatio = goal && (currentProgress / goal);
  const estimatedCompletionTs = completionRatio
    && (
      (
        ((Date.now() - objective.createdAt) * (goal - initialProgress))
        / (currentProgress - initialProgress)
      )
      + objective.createdAt
    );
  const estimatedCompletionDate = estimatedCompletionTs && DateTime.fromJSDate(new Date(estimatedCompletionTs));
  return estimatedCompletionDate;
};

const getObjectiveStatusColor = (objective: Objective) => {
  const today = userNow();
  const dueDate = objective.dueDate ? DateTime.fromISO(objective.dueDate) : null;
  const estimatedCompletionDate = getEstimatedCompletionDate(objective);
  const isLate = isObjectiveBehind(objective, today);
  const isEstimatedLate = !isLate
    && !!estimatedCompletionDate
    && !!dueDate?.isValid
    && estimatedCompletionDate > dueDate;
  return isLate ? '#ff0000' : (isEstimatedLate ? '#dfa901' : '#5b995b');
};

const objectiveMatchesAssignees = (
  objective: Pick<Objective, 'assignee'>,
  assignees?: string[],
) => {
  if (!assignees?.length) {
    return true;
  }
  const includeUnassigned = assignees.includes(UNASSIGNED_FILTER_VALUE);
  const ids = assignees.filter(id => id !== UNASSIGNED_FILTER_VALUE);
  if (includeUnassigned && !objective.assignee) {
    return true;
  }
  return !!objective.assignee && ids.includes(objective.assignee);
};

const sortObjectivesByDueDate = (objectives?: Objective[] | null) => (
  [...(objectives || [])].sort((o1, o2) => {
    if (!o1.dueDate) return o2.dueDate ? 1 : 0;
    if (!o2.dueDate) return -1;
    return o1.dueDate < o2.dueDate ? -1 : 1;
  })
);

const getObjectivesInRange = (objectives: Objective[], fromId: string, toId: string): Objective[] => (
  getItemsInRange(objectives, fromId, toId)
);

interface ApplyObjectiveSelectionParams {
  displayedObjectives: Objective[];
  lastSelectedObjectiveId?: string;
  objective: Objective;
  selectedObjectives: Objective[];
  shiftKey: boolean;
}

const applyObjectiveSelection = ({
  displayedObjectives,
  lastSelectedObjectiveId,
  objective,
  selectedObjectives,
  shiftKey,
}: ApplyObjectiveSelectionParams): {
  lastSelectedObjectiveId?: string;
  selectedObjectives: Objective[];
} => {
  const result = applyRangeSelection({
    displayedItems: displayedObjectives,
    item: objective,
    lastSelectedId: lastSelectedObjectiveId,
    selectedItems: selectedObjectives,
    shiftKey,
  });
  return {
    lastSelectedObjectiveId: result.lastSelectedId,
    selectedObjectives: result.selectedItems,
  };
};

export {
  applyObjectiveSelection,
  currentObjectiveValue,
  formatObjectiveProgress,
  getEstimatedCompletionDate,
  getObjectiveStatusColor,
  getObjectivesInRange,
  getProgressRatio,
  goalKindOf,
  isClosedObjectiveStatus,
  isObjectiveBehind,
  objectiveMatchesAssignees,
  objectiveStatus,
  sortObjectivesByDueDate,
};
